import React, { useCallback, useState } from "react";
import { Select, DatePicker, ConfigProvider, Alert } from "antd";
import datePickerLocale from "antd/lib/locale/pt_BR";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";

import Card from "../../components/Card";
import BreadCrumbNavigation from "../../components/DataTable/TopSection/BreadCrumbNavigation";
import Loading from "../../components/Loading";
import DSButton from "../../components/DSButton";
import { DSInput } from "../../components/DSInput";
import { InputWrapper } from "../../components/InputWrapper";
import api from "../../services/api";
import { formatCalendarDay, pickerValueToDate } from "../../utils/formatDate";
import { notifyError, notifySucces } from "../../utils/notifications";
import { validaCpf } from "../../utils/validations";
import ReserveCancelModal from "../solicitation/SellandPayModal/sellCancelModal";
import * as S from "./style";
import UsersTable from "./UsersTable";

const { Option } = Select;

function getDocLabel(doc) {
  if (doc === "cpf") return "CPF";
  if (doc === "client_id") return "ID do cliente";
  return doc;
}

function ListBalanceReserve() {
  const [data, setData] = useState([]);
  const [formSteps, setFormSteps] = useState(0);
  const [currentDoc, setCurrentDoc] = useState("cpf");
  const [currentMask, setCurrentMask] = useState("999.999.999-99");
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [idClient, setIdClient] = useState(null);
  const [listClientAccount, setListClientAccount] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading] = useState(false);
  const [searchData, setSearchData] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [modalCancelReserveOpen, setmodalCancelReserveOpen] = useState(false);
  const [dateInit, setDateInit] = useState();
  const [dateFim, setDateFim] = useState();
  const [dateInitConfirmation, setDateInitConfirmation] = useState();
  const [dateFimConfirmation, setDateFimConfirmacao] = useState();

  const handleTab = useCallback((doc) => {
    if (doc === "cpf") setCurrentMask("999.999.999-99");
    if (doc === "cnpj") setCurrentMask("99.999.999/9999-99");
    if (doc === "cartao") setCurrentMask("9999 9999 9999 9999");
    if (doc === "client_id") setCurrentMask("99999999999999999");
    setCurrentDoc(doc);
  }, []);

  const onChangeLastYearRangeDate = useCallback((date, setFieldValue) => {
    const d = pickerValueToDate(date);
    if (!d) return;
    setFieldValue("lastYear", formatCalendarDay(d));
    setSearchData(false);
  }, []);

  const onChangeTodayRangeDate = useCallback((date, setFieldValue) => {
    const d = pickerValueToDate(date);
    if (!d) return;
    setFieldValue("today", formatCalendarDay(d));
    setSearchData(false);
  }, []);

  const onChangeLastYearRangeDateConfirm = useCallback(
    (date, setFieldValue) => {
      const d = pickerValueToDate(date);
      if (!d) return;
      setFieldValue("lastYearConfirm", formatCalendarDay(d));
      setSearchData(false);
    },
    []
  );

  const onChangeTodayRangeDateConfirm = useCallback((date, setFieldValue) => {
    const d = pickerValueToDate(date);
    if (!d) return;
    setFieldValue("todayConfirm", formatCalendarDay(d));
    setSearchData(false);
  }, []);

  const handleSubmit = (value) => {
    setLoadingTable(true);
    setSearchData(false);
    setData(null);
    setDateInit(value.lastYear);
    setDateFim(value.today);
    setDateInitConfirmation(value.lastYearConfirm);
    setDateFimConfirmacao(value.todayConfirm);

    return new Promise((resolve, reject) => {
      if (selectedAccount == null || selectedAccount === "0") {
        reject(new Error("Informe a conta do portador"));
      } else {
        resolve();
      }
    })
      .then(() => {
        return api.get("get_reserve_balance", {
          client_id: idClient,
          dataCriacaoInicial: value.lastYear,
          dataCriacaoFinal: value.today,
          dataUtilizacaoFinal: value.todayConfirm,
          dataUtilizacaoInicial: value.lastYearConfirm,
        });
      })
      .then((res) => {
        setData(res.data);
      })
      .catch((error) => {
        notifyError(error.message);
        setData(null);
      })
      .finally(() => {
        setSearchData(true);
        setLoadingTable(false);
      });
  };

  function setPlaceholder() {
    let text = "";
    if (currentDoc === "cpf") text = "do CPF";
    if (currentDoc === "cnpj") text = "do CNPJ";
    if (currentDoc === "cartao") text = "do Cartão";
    if (currentDoc === "client_id") text = "do ID do Cliente";
    return `Digite o número ${text}`;
  }

  const reloadPage = () => {
    setLoadingTable(true);
    setSearchData(false);
    setData(null);
    api
      .get("get_reserve_balance", {
        client_id: idClient,
        dataCriacaoInicial: dateInit,
        dataCriacaoFinal: dateFim,
        dataUtilizacaoFinal: dateFimConfirmation,
        dataUtilizacaoInicial: dateInitConfirmation,
      })
      .then((res) => {
        setData(res.data);
      })
      .catch((error) => {
        notifyError(error.message);
        setData(null);
      })
      .finally(() => {
        setSearchData(true);
        setLoadingTable(false);
      });
  };

  const handleOpenModal = useCallback(
    async (nsuOrigem) => {
      api
        .put("cancel_reserve_balance", {
          client_id: idClient,
          dataHoraTerminal: new Date().toISOString(),
          infoData: data?.filter((item) => item.nsuOrigem === nsuOrigem),
        })
        .then((res) => {
          setData(res.data);
          notifySucces(res.data.message);
        })
        .catch((error) => {
          notifyError(error.message);
          setData(null);
        })
        .finally(() => {
          setSearchData(true);
          setLoadingTable(false);
        });
    },
    [data, idClient]
  );

  const closeModalCancelReserve = useCallback(() => {
    setmodalCancelReserveOpen(false);
  }, []);

  const handleResetSearch = async () => {
    setFormSteps(0);
    setIdClient(null);
    setSearchData(false);
    setSelectedAccount(null);
    setListClientAccount([]);
    handleTab("cpf");
  };

  const handleStepOne = async (values) => {
    try {
      setLoadingBtn(true);
      const response = await api.get("getAccount", {
        dataDocument: values.doc.replace(/[./-]/g, "").replace(/\s/g, ""),
        document: currentDoc,
      });

      const accountsData = Array.isArray(response.data)
        ? response.data
        : response.data
        ? [response.data]
        : [];

      if (accountsData.length === 0) {
        throw new Error("Cliente não encontrado");
      }

      const formattedAccounts = accountsData.map((item) => ({
        id: item.id,
        accountSelect: item.accountSelect,
        name: item.name,
      }));

      setListClientAccount(formattedAccounts);

      // Correção: acessa o primeiro elemento do array [0]
      if (formattedAccounts.length === 1) {
        const singleAccount = formattedAccounts[0];
        setIdClient(singleAccount.id);
        setSelectedAccount(singleAccount.id);
      } else {
        setIdClient(null);
        setSelectedAccount(null);
      }

      setTimeout(() => {
        setLoadingBtn(false);
        setFormSteps(1);
      }, 1000);
    } catch (error) {
      setLoadingBtn(false);
      notifyError(error.message);
    }
  };

  const handleAccountSelected = useCallback(async (value) => {
    setIdClient(value);
    setSelectedAccount(value);
  }, []);

  return (
    <>
      <S.Container data-testid="listagem-reserva-de-saldo-page">
        <BreadCrumbNavigation tableName="Listagem de Reserva de Saldo" />

        {/* Botão Nova Pesquisa com DSButton estilizado */}
        {formSteps > 0 && (
          <div className="select-btn" style={{ marginBottom: "15px" }}>
            <DSButton
              type="button"
              buttonType="secondary"
              isCancelType
              buttonText="Nova Pesquisa"
              onClick={handleResetSearch}
              height={30}
              style={{ padding: "0.4rem 1.5rem" }}
              dataTestId="btn-nova-pesquisa-reserva"
            />
          </div>
        )}

        <Card title="Listagem de Reserva de Saldo" loading={loading}>
          {formSteps === 0 && (
            <Formik
              initialValues={{ doc: "" }}
              validationSchema={Yup.object({
                doc: Yup.string()
                  .required(`${getDocLabel(currentDoc)} é obrigatório`)
                  .test(
                    "test_cpf",
                    `Informe um ${getDocLabel(currentDoc)} válido`,
                    (value) => {
                      if (currentDoc === "client_id") return true;
                      if (currentDoc === "cpf") {
                        if (value === undefined || value === null) return false;
                        const cpfFormated = value
                          .split(".")
                          .join("")
                          .replace("-", "");
                        return validaCpf(cpfFormated);
                      }
                    }
                  ),
              })}
              onSubmit={handleStepOne}
            >
              {({ setErrors }) => (
                <S.SearchForm>
                  <S.CustomTabs
                    tabs={[
                      {
                        title: "CPF",
                        id: "cpf",
                        disabled: loadingBtn,
                      },
                      {
                        title: "ID do cliente",
                        id: "client_id",
                        disabled: loadingBtn,
                      },
                    ]}
                    onClick={(tabId) => {
                      handleTab(tabId);
                      setErrors({ doc: undefined });
                    }}
                  />
                  <Field name="doc">
                    {({ field, meta }) => (
                      <InputWrapper
                        error={
                          meta.touched && meta.error ? meta.error : undefined
                        }
                        isRequired
                        name={field.name}
                      >
                        <DSInput
                          disabled={loadingBtn}
                          mask={currentMask}
                          maskChar={null}
                          autoComplete="off"
                          touched={meta.touched}
                          error={!!meta.error}
                          placeholder={setPlaceholder()}
                          {...field}
                          data-testid="input-doc-reserva"
                        />
                      </InputWrapper>
                    )}
                  </Field>
                  <DSButton
                    type="submit"
                    buttonType="primary"
                    buttonText="Buscar"
                    isLoading={loadingBtn}
                    isDisabled={loadingBtn}
                    height={30}
                    style={{ padding: "0.4rem 1.5rem" }}
                    dataTestId="btn-buscar-reserva-passo1"
                  />
                </S.SearchForm>
              )}
            </Formik>
          )}

          {formSteps !== 0 && (
            <div className="flex">
              {/* Exibição garantida do Select com indexação [0] do array */}
              {listClientAccount.length > 0 && (
                <div>
                  <p style={{ marginBottom: "5px" }}>
                    <strong>* Conta:</strong>
                  </p>
                  <S.FormAccount>
                    {listClientAccount.length > 1 ? (
                      <Select
                        placeholder="Selecionar conta"
                        onChange={handleAccountSelected}
                        value={selectedAccount || undefined}
                        style={{ width: "300px" }}
                        data-testid="select-conta-reserva"
                      >
                        {listClientAccount.map((item) => (
                          <Option value={item.id} key={item.id}>
                            {item.accountSelect}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <Select
                        disabled
                        value={listClientAccount[0]?.id}
                        style={{ width: "300px" }}
                        data-testid="select-conta-reserva-unica"
                      >
                        <Option value={listClientAccount[0]?.id}>
                          {listClientAccount[0]?.accountSelect}
                        </Option>
                      </Select>
                    )}
                  </S.FormAccount>
                </div>
              )}

              <Formik
                initialValues={{
                  lastYear: null,
                  today: null,
                  lastYearConfirm: null,
                  todayConfirm: null,
                }}
                onSubmit={handleSubmit}
              >
                {({ setFieldValue }) => (
                  <Form style={{ display: "flex", alignItems: "flex-end" }}>
                    <ConfigProvider locale={datePickerLocale}>
                      <div className="select-date" style={{ marginLeft: "20px" }}>
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }}>
                            Data de Solicitação:
                          </span>
                        </S.InputsLabel>
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }}>
                            Data Inicial
                          </span>
                        </S.InputsLabel>
                        <Field name="lastYear">
                          {({ meta }) => (
                            <DatePicker
                              defaultValue={null}
                              data-testid="datepicker-solicitacao-inicio"
                              className={`${
                                meta.touched && meta.error ? "error-id" : ""
                              }`}
                              placeholder="Selecione a data"
                              format="DD/MM/YYYY"
                              allowClear={false}
                              showToday={false}
                              onChange={(e) =>
                                onChangeLastYearRangeDate(e, setFieldValue)
                              }
                            />
                          )}
                        </Field>
                      </div>

                      <div
                        className="select-date"
                        style={{ marginLeft: "8px" }}
                      >
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }} />
                        </S.InputsLabel>
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }}>Data Final</span>
                        </S.InputsLabel>
                        <Field name="today">
                          {({ meta }) => (
                            <DatePicker
                              defaultValue={null}
                              data-testid="datepicker-solicitacao-fim"
                              className={`${
                                meta.touched && meta.error ? "error-id" : ""
                              }`}
                              placeholder="Selecione a data"
                              format="DD/MM/YYYY"
                              allowClear={false}
                              showToday={false}
                              onChange={(e) =>
                                onChangeTodayRangeDate(e, setFieldValue)
                              }
                            />
                          )}
                        </Field>
                      </div>

                      <div
                        className="select-date"
                        style={{ marginLeft: "20px" }}
                      >
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }}>
                            Data de Confirmação:
                          </span>
                        </S.InputsLabel>
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }}>
                            Data Inicial
                          </span>
                        </S.InputsLabel>
                        <Field name="lastYearConfirm">
                          {({ meta }) => (
                            <DatePicker
                              defaultValue={null}
                              data-testid="datepicker-confirmacao-inicio"
                              className={`${
                                meta.touched && meta.error ? "error-id" : ""
                              }`}
                              placeholder="Selecione a data"
                              format="DD/MM/YYYY"
                              allowClear={false}
                              showToday={false}
                              onChange={(e) =>
                                onChangeLastYearRangeDateConfirm(
                                  e,
                                  setFieldValue
                                )
                              }
                            />
                          )}
                        </Field>
                      </div>

                      <div
                        className="select-date"
                        style={{ marginLeft: "8px" }}
                      >
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }} />
                        </S.InputsLabel>
                        <S.InputsLabel>
                          <span style={{ marginRight: "5px" }}>Data Final</span>
                        </S.InputsLabel>
                        <Field name="todayConfirm">
                          {({ meta }) => (
                            <DatePicker
                              defaultValue={null}
                              data-testid="datepicker-confirmacao-fim"
                              className={`${
                                meta.touched && meta.error ? "error-id" : ""
                              }`}
                              placeholder="Selecione a data"
                              format="DD/MM/YYYY"
                              allowClear={false}
                              showToday={false}
                              onChange={(e) =>
                                onChangeTodayRangeDateConfirm(e, setFieldValue)
                              }
                            />
                          )}
                        </Field>
                      </div>
                    </ConfigProvider>

                    <div className="select-btn" style={{ marginLeft: "20px" }}>
                      <DSButton
                        type="submit"
                        buttonType="primary"
                        buttonText="Pesquisar"
                        height={30}
                        style={{ padding: "0.4rem 1.5rem" }}
                        dataTestId="btn-pesquisar-datas-reserva"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          <div className="select-table">
            {loadingTable && <Loading />}
            {searchData && data?.length > 0 && (
              <UsersTable data={data} handleOpenModal={handleOpenModal} />
            )}
            {searchData && (!data || data?.length === 0) && (
              <div className="alert-table">
                <Alert
                  message="Não há dados para as datas selecionadas"
                  type="warning"
                  showIcon
                />
              </div>
            )}
          </div>
        </Card>
      </S.Container>

      {modalCancelReserveOpen && (
        <ReserveCancelModal
          onCancel={closeModalCancelReserve}
          reloadPage={reloadPage}
        />
      )}
    </>
  );
}

export default React.memo(ListBalanceReserve);